import React, { ReactNode, useState } from 'react';
import { SvgIcon } from '../Icon';

import { BannerStyle, BannerVariant } from './types';
import {
  BannerCloseButton,
  BannerContent,
  RightSide,
  StyledBanner,
} from './BannerUI';
import { isNil } from 'lodash';
import IconWrapper from '../Icon/IconWrapper';
import { getVariantIcon } from './BannerUtils';
import { iconList } from '../Icon/iconList';
import { MaybeNil } from '../../utils/interfaces';

const BANNER_STYLE_CONFIG = {
  compact: {
    iconSize: 20,
    iconXSize: 16,
  },
  regular: {
    iconSize: 24,
    iconXSize: 18,
  },
};

/**
 * Interface for Banner component props
 *
 * @interface IProps
 * @extends {React.ComponentProps<typeof StyledBanner>}
 */
export interface IProps extends React.ComponentProps<typeof StyledBanner> {
  /**
   * The visual style variant of the banner
   * Determines the color scheme and default icon
   * Possible values: 'error', 'warning', 'info', 'disabled'
   */
  variant: BannerVariant;

  /**
   * Custom callback function triggered when the close button is clicked
   * If provided, this function will be called instead of the default close behavior
   */
  close?: () => void;

  /**
   * Additional CSS class name(s) to apply to the banner component
   * Can be used for custom styling
   */
  className?: string;

  /**
   * Determines the size and density of the banner
   * 'regular': Standard size with normal icon sizes
   * 'compact': Smaller size with reduced icon sizes
   * @default 'regular'
   */
  bannerStyle?: BannerStyle;

  /**
   * Custom icon to override the default icon determined by the variant
   * Must be a key from the iconList object
   */
  icon?: keyof typeof iconList;

  /**
   * If true, enables the banner to be closed/dismissed without providing a custom close handler
   * Uses an internal state to handle closing
   * @default false
   */
  selfClosing?: boolean;

  /**
   * Custom component to render on the right side of the banner
   * If provided, this will replace the close button
   */
  rightSideComponent?: MaybeNil<ReactNode>;

  /**
   * If true, centers the content horizontally within the banner
   * @default false
   */
  centerContent?: boolean;
}

/**
 * Banner
 *
 * A versatile notification component that can display messages with different visual styles.
 * Supports multiple variants (error, warning, info, disabled) and styling options.
 *
 * @example
 * // Basic info banner
 * <Banner variant="info">This is an informational message</Banner>
 *
 * @example
 * // Warning banner with self-closing capability
 * <Banner variant="warning" selfClosing={true}>
 *   This is a warning that can be dismissed
 * </Banner>
 *
 * @example
 * // Compact error banner with custom close handler
 * <Banner
 *   variant="error"
 *   bannerStyle="compact"
 *   close={() => console.info('Banner closed')}
 * >
 *   An error occurred
 * </Banner>
 *
 * @example
 * // Banner with custom right-side content
 * <Banner
 *   variant="info"
 *   rightSideComponent={<button onClick={handleAction}>Action</button>}
 * >
 *   This banner has a custom action
 * </Banner>
 *
 * @returns {JSX.Element|null} The rendered Banner component or null if closed
 */

const Banner = ({
  variant,
  close,
  className,
  bannerStyle = 'regular',
  icon,
  selfClosing = false,
  centerContent = false,
  rightSideComponent,
  children,
}: IProps): JSX.Element | null => {
  const [closed, setClosed] = useState(false);
  if (closed) {
    return null;
  }
  const handleClose = () => setClosed(!closed);
  return (
    <StyledBanner className={className} $variant={variant}>
      <IconWrapper size={BANNER_STYLE_CONFIG[bannerStyle].iconSize}>
        <SvgIcon
          name={icon || getVariantIcon(variant)}
          customFill="currentColor"
        />
      </IconWrapper>
      <BannerContent $centerContent={centerContent}>{children}</BannerContent>
      {(!!close || selfClosing) && !rightSideComponent && (
        <BannerCloseButton
          onClick={close || handleClose}
          size={BANNER_STYLE_CONFIG[bannerStyle].iconXSize}
        >
          <SvgIcon name="icon-x" />
        </BannerCloseButton>
      )}
      {!isNil(rightSideComponent) && (
        <RightSide>{rightSideComponent}</RightSide>
      )}
    </StyledBanner>
  );
};

export default Banner;
